/**
 * iGaming sub-affiliate / master-affiliate nested commissions (#155) —
 * operator UI.
 *
 * Composition:
 *   - KPI strip with counts (links shown, sum bps, preview cut).
 *   - New-link card: form for creating/upserting a master→sub link with a
 *     live commission preview.
 *   - Subs-under-master card: look up a master, list their active subs.
 *   - Master-of-sub card: look up a sub, show their current master + link.
 *
 * Brand: emerald→sky headline gradient, zinc borders, 6–8px rounded,
 * light theme only, no emojis.
 *
 * Data layer lives in `./api`; the page itself is render + wiring.
 */
import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	bpsToPercent,
	calculateCut,
	createLink,
	deleteLink,
	fetchMasterOfSub,
	fetchSubsUnderMaster,
	formatCents,
	formatCreatedAt,
	percentToBps,
	previewCut,
	sortSubs,
	subAffiliateQueryKeys,
	type CalculateResponse,
	type CreateLinkInput,
	type GetSubMasterResponse,
	type ListSubsResponse,
	type SubAffiliateLink,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — mirror the dash brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

const DEFAULT_PREVIEW_PAYOUT_CENTS = 10_000; // $100 sample sub payout.

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function IgamingSubAffiliatePage() {
	const queryClient = useQueryClient();

	// New-link form state.
	const [formSubId, setFormSubId] = useState("");
	const [formMasterId, setFormMasterId] = useState("");
	const [formPercent, setFormPercent] = useState("25");

	// Lookup state.
	const [lookupMasterId, setLookupMasterId] = useState("");
	const [lookupSubId, setLookupSubId] = useState("");

	// ---- Queries ---------------------------------------------------------
	const subsQuery = useQuery<ListSubsResponse>({
		queryKey: subAffiliateQueryKeys.subsUnder(lookupMasterId.trim()),
		queryFn: () => fetchSubsUnderMaster(lookupMasterId.trim()),
		enabled: lookupMasterId.trim().length > 0,
	});

	const masterQuery = useQuery<GetSubMasterResponse>({
		queryKey: subAffiliateQueryKeys.masterOf(lookupSubId.trim()),
		queryFn: () => fetchMasterOfSub(lookupSubId.trim()),
		enabled: lookupSubId.trim().length > 0,
	});

	const subs = useMemo<SubAffiliateLink[]>(
		() => sortSubs(subsQuery.data?.subs ?? []),
		[subsQuery.data],
	);

	// KPI stats.
	const sumBps = useMemo(
		() => subs.reduce((acc, s) => acc + s.master_bps, 0),
		[subs],
	);

	// Preview — based on current form values.
	const previewBps = useMemo(() => {
		const bps = percentToBps(formPercent);
		return bps ?? 0;
	}, [formPercent]);
	const previewCutCents = useMemo(
		() => previewCut(DEFAULT_PREVIEW_PAYOUT_CENTS, previewBps),
		[previewBps],
	);

	// ---- Mutations -------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: createLink,
		onSuccess: (res) => {
			toast.success(
				`Linked ${res.link.sub_affiliate_id} → ${res.link.master_affiliate_id}.`,
			);
			queryClient.invalidateQueries({
				queryKey: subAffiliateQueryKeys.all,
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "Create failed."),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteLink,
		onSuccess: () => {
			toast.success("Link removed.");
			queryClient.invalidateQueries({
				queryKey: subAffiliateQueryKeys.all,
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "Delete failed."),
	});

	const calcMutation = useMutation({
		mutationFn: calculateCut,
		onSuccess: (res: CalculateResponse) => {
			if (res.master_affiliate_id === null) {
				toast.success("No master assigned — cut is $0.00.");
			} else {
				toast.success(
					`Master ${res.master_affiliate_id} earns ${formatCents(
						res.master_cut_cents,
					)}.`,
				);
			}
		},
		onError: (err: Error) => toast.error(err.message ?? "Calc failed."),
	});

	const busy =
		createMutation.isPending ||
		deleteMutation.isPending ||
		calcMutation.isPending;

	// ---- Handlers --------------------------------------------------------
	const handleCreate = useCallback((): void => {
		if (busy) return;
		const sub = formSubId.trim();
		const master = formMasterId.trim();
		const bps = percentToBps(formPercent);
		if (!sub) {
			toast.error("Sub affiliate id is required.");
			return;
		}
		if (!master) {
			toast.error("Master affiliate id is required.");
			return;
		}
		if (sub === master) {
			toast.error("Sub and master must differ.");
			return;
		}
		if (bps === null) {
			toast.error("Percent must be 0–100 (e.g. 25 or 12.5).");
			return;
		}
		const input: CreateLinkInput = {
			sub_affiliate_id: sub,
			master_affiliate_id: master,
			master_bps: bps,
		};
		createMutation.mutate(input);
	}, [busy, formSubId, formMasterId, formPercent, createMutation]);

	const handleDelete = useCallback(
		(link: SubAffiliateLink) => {
			if (busy) return;
			if (typeof window !== "undefined") {
				const ok = window.confirm(
					`Remove link ${link.sub_affiliate_id} → ${link.master_affiliate_id}?`,
				);
				if (!ok) return;
			}
			deleteMutation.mutate(link.id);
		},
		[busy, deleteMutation],
	);

	const handleCalcForSub = useCallback(
		(subId: string) => {
			if (!subId) return;
			calcMutation.mutate({
				sub_affiliate_id: subId,
				sub_payout_cents: DEFAULT_PREVIEW_PAYOUT_CENTS,
			});
		},
		[calcMutation],
	);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------
	return (
		<main
			data-testid="igaming-sub-affiliate-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<KpiStrip
				linksShown={subs.length}
				sumBps={sumBps}
				previewCutCents={previewCutCents}
				loading={subsQuery.isLoading}
			/>

			<NewLinkCard
				subId={formSubId}
				masterId={formMasterId}
				percent={formPercent}
				setSubId={setFormSubId}
				setMasterId={setFormMasterId}
				setPercent={setFormPercent}
				busy={busy}
				previewBps={previewBps}
				previewCutCents={previewCutCents}
				onCreate={handleCreate}
			/>

			<MasterLookupCard
				masterId={lookupMasterId}
				setMasterId={setLookupMasterId}
				data={subsQuery.data}
				loading={subsQuery.isLoading}
				error={subsQuery.error?.message ?? null}
				busy={busy}
				subs={subs}
				onDelete={handleDelete}
				onCalc={handleCalcForSub}
			/>

			<SubLookupCard
				subId={lookupSubId}
				setSubId={setLookupSubId}
				data={masterQuery.data}
				loading={masterQuery.isLoading}
				error={masterQuery.error?.message ?? null}
				busy={busy}
				onDelete={handleDelete}
				onCalc={handleCalcForSub}
			/>
		</main>
	);
}

export default IgamingSubAffiliatePage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				iGaming
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Sub-affiliate commissions
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Let a master affiliate earn a share of what their referred sub
				affiliates generate. One level of nesting only — a master is never
				also a sub. The master cut is computed as a percentage of the sub's
				earned payout.
			</p>
		</header>
	);
}

function KpiStrip(props: {
	linksShown: number;
	sumBps: number;
	previewCutCents: number;
	loading: boolean;
}) {
	const cells = [
		{ label: "Subs shown", value: props.linksShown.toString() },
		{
			label: "Average cut",
			value:
				props.linksShown === 0
					? "—"
					: bpsToPercent(Math.round(props.sumBps / props.linksShown)),
		},
		{
			label: "Preview ($100 sub)",
			value: formatCents(props.previewCutCents),
		},
	];
	return (
		<section
			aria-label="Sub-affiliate statistics"
			className="grid grid-cols-1 gap-3 sm:grid-cols-3"
			data-testid="kpi-strip"
		>
			{cells.map((c) => (
				<div key={c.label} className={`${CARD} flex flex-col gap-1 p-4`}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : c.value}
					</p>
				</div>
			))}
		</section>
	);
}

function NewLinkCard(props: {
	subId: string;
	masterId: string;
	percent: string;
	setSubId: (s: string) => void;
	setMasterId: (s: string) => void;
	setPercent: (s: string) => void;
	busy: boolean;
	previewBps: number;
	previewCutCents: number;
	onCreate: () => void;
}) {
	const invalidPercent = percentToBps(props.percent) === null;
	const selfLink =
		props.subId.trim().length > 0 &&
		props.masterId.trim().length > 0 &&
		props.subId.trim() === props.masterId.trim();
	return (
		<section className={`${CARD} flex flex-col`} data-testid="new-link-card">
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					New master link
				</h2>
				<p className="text-sm text-zinc-500">
					Assign a master to a sub affiliate. Re-submitting for the same sub
					replaces the previous master (the old link is soft-deleted).
				</p>
			</header>
			<div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-3">
				<div className="flex flex-col gap-2">
					<label htmlFor="sa-sub-id" className={LABEL}>
						Sub affiliate id
					</label>
					<input
						id="sa-sub-id"
						name="subAffiliateId"
						type="text"
						className={INPUT}
						placeholder="aff_sub_42"
						value={props.subId}
						onChange={(e) => props.setSubId(e.currentTarget.value)}
						data-testid="form-sub-id"
					/>
				</div>
				<div className="flex flex-col gap-2">
					<label htmlFor="sa-master-id" className={LABEL}>
						Master affiliate id
					</label>
					<input
						id="sa-master-id"
						name="masterAffiliateId"
						type="text"
						className={INPUT}
						placeholder="aff_master_7"
						value={props.masterId}
						onChange={(e) => props.setMasterId(e.currentTarget.value)}
						data-testid="form-master-id"
					/>
				</div>
				<div className="flex flex-col gap-2">
					<label htmlFor="sa-percent" className={LABEL}>
						Master cut (%)
					</label>
					<input
						id="sa-percent"
						name="masterPercent"
						type="text"
						inputMode="decimal"
						className={INPUT}
						placeholder="25"
						value={props.percent}
						onChange={(e) => props.setPercent(e.currentTarget.value)}
						data-testid="form-percent"
					/>
				</div>
			</div>
			<div className="flex flex-col gap-3 border-t border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div
					className="flex flex-col text-sm text-zinc-600"
					data-testid="preview-block"
				>
					<p>
						Preview: on a{" "}
						<span className="tabular-nums">
							{formatCents(DEFAULT_PREVIEW_PAYOUT_CENTS)}
						</span>{" "}
						sub payout at{" "}
						<span className="tabular-nums">
							{bpsToPercent(props.previewBps)}
						</span>
						, the master earns{" "}
						<span className="font-semibold text-zinc-900 tabular-nums">
							{formatCents(props.previewCutCents)}
						</span>
						.
					</p>
					{invalidPercent ? (
						<p className="text-red-600" role="alert">
							Percent must be between 0 and 100.
						</p>
					) : null}
					{selfLink ? (
						<p className="text-red-600" role="alert">
							Master and sub must be different affiliates.
						</p>
					) : null}
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={props.onCreate}
						disabled={
							props.busy ||
							invalidPercent ||
							selfLink ||
							props.subId.trim().length === 0 ||
							props.masterId.trim().length === 0
						}
						data-testid="create-link-btn"
					>
						Create link
					</button>
				</div>
			</div>
		</section>
	);
}

function MasterLookupCard(props: {
	masterId: string;
	setMasterId: (s: string) => void;
	data: ListSubsResponse | undefined;
	loading: boolean;
	error: string | null;
	busy: boolean;
	subs: readonly SubAffiliateLink[];
	onDelete: (link: SubAffiliateLink) => void;
	onCalc: (subId: string) => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="master-lookup-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-1 flex-col gap-2">
					<label htmlFor="sa-lookup-master" className={LABEL}>
						Master affiliate id
					</label>
					<input
						id="sa-lookup-master"
						name="lookupMasterId"
						type="text"
						className={INPUT}
						placeholder="aff_master_7"
						value={props.masterId}
						onChange={(e) => props.setMasterId(e.currentTarget.value)}
						data-testid="lookup-master-input"
					/>
				</div>
			</header>

			{props.masterId.trim().length === 0 ? (
				<div className="p-5 text-sm text-zinc-500">
					Enter a master id to list their active sub affiliates.
				</div>
			) : props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading subs.</div>
			) : props.subs.length === 0 ? (
				<div className="p-5 text-sm text-zinc-500">
					No active subs under this master.
				</div>
			) : (
				<div className="overflow-hidden rounded-b-[8px]">
					<table
						className="w-full text-left text-sm"
						data-testid="subs-table"
					>
						<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-3 py-2">Sub</th>
								<th className="px-3 py-2">Cut</th>
								<th className="px-3 py-2">Added</th>
								<th className="px-3 py-2 text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{props.subs.map((s) => (
								<tr key={s.id} data-testid={`sub-row-${s.id}`}>
									<td className="px-3 py-2 font-medium text-zinc-900">
										{s.sub_affiliate_id}
									</td>
									<td className="px-3 py-2 tabular-nums text-zinc-700">
										{bpsToPercent(s.master_bps)}
									</td>
									<td className="px-3 py-2 text-zinc-500">
										{formatCreatedAt(s.created_at)}
									</td>
									<td className="px-3 py-2">
										<div className="flex justify-end gap-2">
											<button
												type="button"
												className={BTN_NEUTRAL}
												onClick={() => props.onCalc(s.sub_affiliate_id)}
												disabled={props.busy}
												data-testid={`calc-btn-${s.id}`}
											>
												Preview cut
											</button>
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => props.onDelete(s)}
												disabled={props.busy}
												data-testid={`delete-btn-${s.id}`}
											>
												Remove
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function SubLookupCard(props: {
	subId: string;
	setSubId: (s: string) => void;
	data: GetSubMasterResponse | undefined;
	loading: boolean;
	error: string | null;
	busy: boolean;
	onDelete: (link: SubAffiliateLink) => void;
	onCalc: (subId: string) => void;
}) {
	const link = props.data?.link ?? null;
	return (
		<section
			className={`${CARD} flex flex-col`}
			data-testid="sub-lookup-card"
		>
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-1 flex-col gap-2">
					<label htmlFor="sa-lookup-sub" className={LABEL}>
						Sub affiliate id
					</label>
					<input
						id="sa-lookup-sub"
						name="lookupSubId"
						type="text"
						className={INPUT}
						placeholder="aff_sub_42"
						value={props.subId}
						onChange={(e) => props.setSubId(e.currentTarget.value)}
						data-testid="lookup-sub-input"
					/>
				</div>
			</header>

			{props.subId.trim().length === 0 ? (
				<div className="p-5 text-sm text-zinc-500">
					Enter a sub id to see their current master (if any).
				</div>
			) : props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading master.</div>
			) : link === null ? (
				<div className="p-5 text-sm text-zinc-500">
					This sub has no active master assigned.
				</div>
			) : (
				<div
					className="flex flex-col gap-3 p-5"
					data-testid="sub-master-block"
				>
					<div className="flex flex-wrap items-center gap-3 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
						<div className="flex flex-col">
							<p className="text-xs uppercase tracking-wide text-zinc-500">
								Master
							</p>
							<p className="text-sm font-semibold text-zinc-900">
								{link.master_affiliate_id}
							</p>
							<p className="text-xs text-zinc-500">
								Since {formatCreatedAt(link.created_at)} · Link {link.id}
							</p>
						</div>
						<span
							className="ml-auto rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
							data-testid="sub-master-pct"
						>
							{bpsToPercent(link.master_bps)}
						</span>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={() => props.onCalc(link.sub_affiliate_id)}
							disabled={props.busy}
							data-testid="sub-preview-btn"
						>
							Preview cut on $100
						</button>
						<button
							type="button"
							className={BTN_DANGER}
							onClick={() => props.onDelete(link)}
							disabled={props.busy}
							data-testid="sub-remove-btn"
						>
							Remove link
						</button>
					</div>
				</div>
			)}
		</section>
	);
}
